import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { UserData, UserMeta, UserProfile } from '../types/userData';
import {
    addAchievement,
    addAksaraCompleted,
    addCourseCompleted,
    addHeart,
    addMoney,
    addXp,
    clearUserData,
    formatHeartRecoveryTime,
    getCurrentStreak,
    getHeartRecoveryInfo,
    getUserData,
    getXpProgress,
    loseHeart,
    purchaseHearts,
    spendMoney,
    updateMetaField,
    updateUserProfile
} from '../utils/userStorage.ts';

interface UserContextType {
    userData: UserData | null;
    isLoading: boolean;
    updateProfile: (updates: Partial<UserProfile>) => boolean;
    completeAksara: (aksaraId: string | number) => boolean;
    earnAchievement: (achievementId: string | number) => boolean;
    completeCourse: (courseId: string) => boolean;
    addMoney: (amount: number) => boolean;
    spendMoney: (amount: number) => boolean;
    addHeart: (amount?: number) => boolean;
    loseHeart: (amount?: number) => boolean;
    addXp: (amount: number) => boolean;
    getXpProgress: () => { currentXp: number, xpToNextLevel: number, totalXp: number, level: number, progressPercent: number };
    purchaseHearts: (amount?: number) => { success: boolean; message: string; cost: number };
    getHeartRecoveryInfo: () => { currentHearts: number; maxHearts: number; timeUntilNextHeart: number; nextHeartTime?: Date; isRecovering: boolean };
    formatHeartRecoveryTime: (milliseconds: number) => string;
    getCurrentStreak: () => number;
    updateMeta: (updates: Partial<UserMeta>) => boolean;
    logout: () => void;
    refreshUserData: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshUserData = () => {
        setIsLoading(true);
        try {
            const data = getUserData();
            setUserData(data);
        } catch (error) {
            console.error('Error refreshing user data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refreshUserData();
    }, []);

    const updateProfile = (updates: Partial<UserProfile>): boolean => {
        const success = updateUserProfile(updates);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const completeAksara = (aksaraId: string | number): boolean => {
        const success = addAksaraCompleted(aksaraId);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const earnAchievement = (achievementId: string | number): boolean => {
        const success = addAchievement(achievementId);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const completeCourse = (courseId: string): boolean => {
        const success = addCourseCompleted(courseId);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleAddMoney = (amount: number): boolean => {
        const success = addMoney(amount);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleSpendMoney = (amount: number): boolean => {
        const success = spendMoney(amount);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleAddHeart = (amount: number = 1): boolean => {
        const success = addHeart(amount);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleLoseHeart = (amount: number = 1): boolean => {
        const success = loseHeart(amount);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleGetCurrentStreak = (): number => {
        return getCurrentStreak();
    };

    const handleAddXp = (amount: number): boolean => {
        const success = addXp(amount);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const handleGetXpProgress = () => {
        return getXpProgress();
    };

    const handlePurchaseHearts = (amount: number = 1) => {
        const result = purchaseHearts(amount);
        if (result.success) {
            refreshUserData();
        }
        return result;
    };

    const handleGetHeartRecoveryInfo = () => {
        return getHeartRecoveryInfo();
    };

    const handleFormatHeartRecoveryTime = (milliseconds: number): string => {
        return formatHeartRecoveryTime(milliseconds);
    };

    const updateMeta = (updates: Partial<UserMeta>): boolean => {
        const success = updateMetaField(updates);
        if (success) {
            refreshUserData();
        }
        return success;
    };

    const logout = (): void => {
        clearUserData();
        setUserData(null);
        // Optionally redirect to welcome page
        window.location.href = '/welcome';
    };

    const value: UserContextType = {
        userData,
        isLoading,
        updateProfile,
        completeAksara,
        earnAchievement,
        completeCourse,
        addMoney: handleAddMoney,
        spendMoney: handleSpendMoney,
        addHeart: handleAddHeart,
        loseHeart: handleLoseHeart,
        addXp: handleAddXp,
        getXpProgress: handleGetXpProgress,
        purchaseHearts: handlePurchaseHearts,
        getHeartRecoveryInfo: handleGetHeartRecoveryInfo,
        formatHeartRecoveryTime: handleFormatHeartRecoveryTime,
        getCurrentStreak: handleGetCurrentStreak,
        updateMeta,
        logout,
        refreshUserData,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
